import {
  OmniElement,
  OmniIconElement,
  OmniLoadingIndicatorElement,
  OmniStyleElement,
  OmniToolbarElement,
  OmniTileElement,
  ImageAPI,
  createRef,
  classMap,
  nothing,
  ref,
  html,
  css,
} from 'omni-ui';

import { BlueprintGraph } from '../../services/blueprint-graph.js';
import * as bpConstants from '../../services/blueprint-graph/constants.js';
import { ToolbarTemplate } from './templates/toolbar.js';

import './campaign-blueprint-toolbox.js';
import './campaign-blueprint-vertex-editor.js';

OmniIconElement.register();
OmniLoadingIndicatorElement.register();
OmniStyleElement.register();
OmniTileElement.register();
OmniToolbarElement.register();

export class CampaignBlueprint extends OmniElement {
  static get properties() {
    return {
      /** external properties */
      env: { type: String },
      context: { type: Object },
      token: { type: String },
      savedData: { type: Array },
      save: { attribute: false },

      /** internal properties */
      blueprintData: { state: true, type: Object },
      /** @todo: De-scoped for Phase 1 */
      // showDetails: { state: true, type: Boolean },
      showToolbox: { state: true, type: Boolean },
      showContextMenu: { state: true, type: Boolean },
      showEditPopup: { state: true, type: Boolean },
      imageLoading: { state: true, type: Boolean },
    };
  }

  static get styles() {
    return [
      ...super.styles,
      css`
        :root {
          --context-menu-top: 0;
          --context-menu-left: 0;
          --edit-popup-top: 0;
          --edit-popup-left: 0;
        }

        :host .module-container {
          margin-bottom: 2.143rem;
          position: relative;
        }

        .toolbar {
          padding: 1.321rem 0;
        }

        .toolbar::part(toolbar) {
          padding-left: 0;
          padding-right: 0;
        }

        .toolbar-left {
          display: flex;
          align-items: center;
          justify-content: flex-start;
        }

        .zoom-button omni-icon {
          --color-icon-lines: var(--color-primary);
        }

        .history-button omni-icon {
          opacity: 0.3;
        }

        .graph-wrapper {
          background: var(--color-pale-grey);
          border: thin solid var(--color-pale-grey);
          position: relative;

          /* Counter omni style padding for selector: .omni .box */
          width: calc(100% + 48px);
          left: -24px;
        }

        .canvas-wrapper:focus {
          /**
           * @todo: this is bad for accessibility but maybe focus individual
           * elements in the canvas?
           */
          outline: none;
        }

        .canvas-loader {
          position: absolute;
          inset: 0;
          z-index: 2; /* stay below popovers but above canvas */
          background: rgba(var(--rgb-primary), 0.05);
          display: flex;
          justify-content: center;
          align-items: center;
          opacity: 0;
          visibility: hidden;
          font-weight: bold;
          backdrop-filter: blur(0.05rem);
          transition: all 0.3s ease;
        }
        .canvas-loader.active {
          opacity: 1;
          visibility: visible;
          backdrop-filter: blur(0.5rem);
          transition: all 0.3s ease;
        }

        .dropdown-content {
          position: absolute;
          top: var(--context-menu-top);
          left: var(--context-menu-left);
          width: 8rem;
          z-index: 9;
        }

        /* Taken from dropdown styles from omni to still use buttons */
        .omni button.dropdown-item {
          background: transparent;
          border: none;
          border-radius: 0;
          box-shadow: none;
        }

        .toolbox-tile {
          width: 32rem;
          max-width: 100%;
          max-height: 60vh;
          z-index: 5;
        }

        .toolbox-tile-blurb {
          margin: 1.7rem 1.4rem;
          color: var(--color-core-medium);
        }

        .toolbox-file-input {
          visibility: hidden;
          position: absolute;
        }

        .edit-popup {
          width: 25vw;
          height: 50vh;
          position: absolute;
          top: var(--edit-popup-top);
          left: var(--edit-popup-left);
          z-index: 10;
        }

        .edit-popup-body {
          padding: 1rem 2rem;
        }
      `,
    ];
  }

  constructor() {
    super();
    this.graphCanvasRef = createRef();
    this.editPopupRef = createRef();
    this.toolboxTileRef = createRef();
    this.toolboxImageInputRef = createRef();

    this.blueprintData = null;
    this.graphEditor = null;
    this.imageAPI = null;
    this.showToolbox = false;
    this.imageLoading = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this._stopDragClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._stopDragClick);
    super.disconnectedCallback();
  }

  async firstUpdated() {
    const omniStyle = this.shadowRoot.querySelector('omni-style');
    const colorDefault = omniStyle
      ? getComputedStyle(omniStyle).getPropertyValue('--rgb-primary')
      : undefined;

    this.imageAPI = new ImageAPI(window.AN.sso.session.sid, this.env);

    const graphConfig = {
      listeners: {
        interaction: this.#handleBlueprintInteraction,
        /** @todo: What's a better way to just disable this without handlers? another option? Is allowing the consumer to prevent default enough? */
        contextmenu: () => {},
        mousedown: this.#handleMouseDown,
        dblclick: this.#handleDblClick,
        init: () => {
          /** @todo Figure out the canvas width error */
          /** @todo Maybe ResizeObserver in BlueprintGraph would be helpful */
          this.requestUpdate(); // trigger update to fix canvas size?
        },
        change: this.#dispatchModuleChange,
        dropbefore: ({ e = {} }) => {
          const dragObjectData = JSON.parse(
            e.dataTransfer.getData('application/json'),
          );
          const { offsetX, offsetY } = e;
          const { x: left, y: top } = fabric.util.transformPoint(
            { x: offsetX, y: offsetY },
            fabric.util.invertTransform(
              this.graphEditor?.canvas.viewportTransform,
            ),
          );
          const defaultFabricOptions = {
            top,
            left,
            originX: 'center',
            originY: 'center',
          };
          const computedOmniStyle = getComputedStyle(
            this.shadowRoot.querySelector('omni-style'),
          );
          switch (dragObjectData.type) {
            case 'text': {
              this.graphEditor?.addText(undefined, {
                ...defaultFabricOptions,
                fill: computedOmniStyle?.getPropertyValue('--color-core-dark'),
              });
              break;
            }
            case 'shape': {
              this.graphEditor?.addShape(
                dragObjectData.shape,
                defaultFabricOptions,
                {
                  fill: computedOmniStyle?.getPropertyValue(
                    '--color-core-dark',
                  ),
                },
              );
              break;
            }
            case 'line': {
              this.graphEditor?.addLine(
                {
                  coords: {
                    tail: { x: left - 50, y: top },
                    head: { x: left + 50, y: top },
                  },
                  arrow: dragObjectData.arrow,
                  style: dragObjectData.style,
                },
                {
                  top,
                  left: left - 50,
                },
              );
              break;
            }
            case 'image': {
              const { value: imageInput } = this.toolboxImageInputRef;

              imageInput.click();
              break;
            }

            default:
              break;
          }
        },
      },
      defaults: {
        controlsColor: `rgba(${colorDefault}, 0.6)`,
        shapeColor: `rgb(${colorDefault})`,
      },
      initialState: this.savedData?.graphData ?? null,
    };

    this.graphEditor = new BlueprintGraph(
      this.graphCanvasRef.value,
      graphConfig,
      this.context.readonly,
    );
  }

  #dispatchModuleChange = () => {
    const { context, graphEditor } = this;
    const { uuid_module: moduleId } = context;

    this.dispatchNewEvent('omni-module-changed', {
      detail: {
        moduleId,
        saveAction: () =>
          this.save({
            graphData: graphEditor.state,
          }),
      },
    });
  };

  /**
   * Hide the context and edit menus on most editor interactions.
   */
  #handleBlueprintInteraction = eventName => {
    /** @todo changing the 'interaction' and 'change' events will break this function up */
    const [isSelection, isObjectUpdate, isMouseDown, isContextMenu] = [
      eventName.startsWith('selection'),
      eventName.startsWith('object'),
      eventName === 'mousedown',
      eventName === 'contextmenu',
    ];

    if (isObjectUpdate) this.#dispatchModuleChange();

    /** @todo: undo this and connect it to the API, when using it there was something a little janky with load/save, duplicating shapes and what not */
    if (isSelection || isObjectUpdate || isMouseDown || isContextMenu) return;
    /** @todo: Determine which specific events should actually hide this */
    this.showContextMenu = false;
    this.showEditPopup = false;
  };

  /** @todo: De-scoped for Phase 1 */
  // #handleShowDetailsChange({ detail: { checked } }) {
  //   this.showDetails = checked;
  // }

  // only called from button in toolbar
  #openToolbox = async ({ target }) => {
    const button = target.closest('button');
    this.showToolbox = true;
    this.showEditPopup = false;
    this.showContextMenu = false;

    // wait until visible
    await this.updateComplete;
    const {
      x,
      y,
      height: buttonHeight,
      width: buttonWidth,
    } = button.getBoundingClientRect();
    const { value: toolbox } = this.toolboxTileRef;
    const { width: toolboxTileWidth } = toolbox.getBoundingClientRect();

    // toolbox is 25vw, just a little less
    const left = x + buttonWidth - toolboxTileWidth;
    const top = y + buttonHeight * 1.5; // a little below the button

    toolbox.style.setProperty('--drag-top', `${top}px`);
    toolbox.style.setProperty('--drag-left', `${left}px`);
  };

  #closeToolbox() {
    this.showToolbox = false;
  }

  #handleHideContextMenu = () => {
    this.showContextMenu = false;
  };

  #handleShowContextMenu = (_vertex, { x, y }) => {
    this.#handleHideContextMenu();
    this.#handleHideEditPopup();

    this.style.setProperty('--context-menu-top', `${y}px`);
    this.style.setProperty('--context-menu-left', `${x}px`);

    this.showContextMenu = true;
  };

  /**
   * @param {MouseEvent} evt - Fabric event
   * @param {BlueprintVertex} vertex - The vertex being clicked
   * @todo: You should be able to get this data from a shape itself
   * @param {fabric.Object} vertex.ref - The ref
   */
  #handleMouseDown = (evt, vertex) => {
    if (evt.button === 2 && vertex) {
      const { layerX: x, layerY: y } = evt;
      this.#handleShowContextMenu(vertex, { x, y });
    } else {
      this.#handleHideContextMenu();
      this.#handleHideEditPopup();
    }
  };

  #handleDblClick = ({ target: vertex }) => {
    if (this.context.readonly) return;

    // ignore lines & images
    /** @todo Would be cool if these handlers only received BlueprintVertex & BleprintEdge objects */
    if (
      vertex &&
      vertex.type?.toLowerCase() !== 'line' &&
      vertex.type?.toLowerCase() !== 'image'
    )
      this.#handleShowEditPopup();
  };

  #handleShowEditPopup = async () => {
    this.showEditPopup = true;
    this.#handleHideContextMenu();

    await this.updateComplete;

    const { x, y } = this.graphEditor.getPopupCoords();
    const { value: tile } = this.editPopupRef;

    tile.style.setProperty('--edit-popup-left', `${x}px`);
    tile.style.setProperty('--edit-popup-top', `${y}px`);
  };

  #handleHideEditPopup = () => {
    this.showEditPopup = false;
  };

  get #toolbarTemplate() {
    const { graphEditor } = this;
    const { history: { canUndo, canRedo } = {} } = graphEditor || {};
    const isReadonly = this.context.readonly;

    return ToolbarTemplate({
      isReadonly,
      canUndo,
      canRedo,
      /** @todo De-scoped for Phase 1 */
      // handleShowDetailsChange: this.#handleShowDetailsChange,
      handleZoomIn: () => {
        graphEditor?.zoomIn();
      },
      handleZoomOut: () => {
        graphEditor?.zoomOut();
      },
      handleRecenter: graphEditor?.recenter,
      handleRedo: graphEditor?.redo,
      handleUndo: graphEditor?.undo,
      handleOpenToolbox: this.#openToolbox,
    });
  }

  /**
   * @todo: Need a listener for the tile drag end so that it can be
   * determined whether or not they've dropped it under the header,
   * and change the tile somehow(?). Use the --drag-[top|left|width|height]
   * variables when you open it and need to reposition it, via the ref and
   * a setProperty... may need to remove the property after they drag again
   * or something?
   */
  #handleToolboxDragEnd = () => {
    // check if it was dropped under the header somehow
  };

  get #toolboxTemplate() {
    if (!this.showToolbox) return nothing;

    return html`
      <omni-style>
        <omni-tile
          ${ref(this.toolboxTileRef)}
          @drag-end=${this.#handleToolboxDragEnd}
          drag-able
          class="toolbox-tile"
          scroll="true"
        >
          <omni-toolbar slot="header">
            Shapes Toolbox
            <button
              slot="end"
              class="button is-text is-shadowless"
              @click=${this.#closeToolbox}
            >
              <omni-icon
                class="is-size-1"
                icon-id="omni:interactive:close"
              ></omni-icon>
            </button>
          </omni-toolbar>
          <campaign-blueprint-toolbox
            .isActive=${this.showToolbox}
            @add-shape=${({ detail: { type } }) => {
              const computedOmniStyle = getComputedStyle(
                this.shadowRoot.querySelector('omni-style'),
              );
              this.graphEditor?.addShape(
                type,
                {},
                {
                  fill: computedOmniStyle?.getPropertyValue(
                    '--color-core-dark',
                  ),
                },
              );
            }}
            @add-text=${() => {
              const computedOmniStyle = getComputedStyle(
                this.shadowRoot.querySelector('omni-style'),
              );
              /** @todo Should we default the text here instead of in the method? */
              this.graphEditor?.addText(undefined, {
                fill: computedOmniStyle?.getPropertyValue('--color-core-dark'),
              });
            }}
            @add-image=${() => {
              /** @todo: This is potentially confusing and feels hacky, triggering the input from the toolbox event, then relying on the change event to upload it and add it to the graph */
              const { value: imageInput } = this.toolboxImageInputRef;

              imageInput.click();
            }}
          ></campaign-blueprint-toolbox>
          <input
            ${ref(this.toolboxImageInputRef)}
            @change=${async ({ target }) => {
              const { files: [file] = [] } = target;
              this.imageLoading = true;
              try {
                const url = await this.imageAPI.createImageURL(file);
                this.graphEditor?.addImage(url);
              } catch (e) {
                /** @todo Handle this error */
                // eslint-disable-next-line no-console
                console.warn('Handle this error', e);
              }
              /** @todo Move these handlers out and use the state? */
              // eslint-disable-next-line no-param-reassign
              target.value = '';
              this.imageLoading = false;
              /** @todo should we accept svgs? */
            }}
            type="file"
            class="toolbox-file-input"
            accept="image/jpeg,image/png,image/gif,image/webp"
          />
        </omni-tile>
      </omni-style>
    `;
  }

  get #editPopupTemplate() {
    if (!this.showEditPopup) return nothing;

    const [{ vertexType } = {}] = this.graphEditor.selection;

    return html`
      <omni-tile scroll ${ref(this.editPopupRef)} class="edit-popup">
        <omni-toolbar slot="header">
          Edit
          <button
            slot="end"
            class="button is-text is-shadowless"
            @click=${this.#handleHideEditPopup}
          >
            <omni-icon
              class="is-size-1"
              icon-id="omni:interactive:close"
            ></omni-icon>
          </button>
        </omni-toolbar>
        <campaign-blueprint-vertex-editor
          .graphEditor=${this.graphEditor}
          .vertexType=${vertexType}
          @vertex-change=${this.#dispatchModuleChange}
        ></campaign-blueprint-vertex-editor>
      </omni-tile>
    `;
  }

  get #contextMenuTemplate() {
    if (!this.showContextMenu) return nothing;

    const { selection } = this.graphEditor;
    const showEdit =
      // can't edit multiple simultaneously
      selection.length === 1 &&
      // can't edit images
      ![bpConstants.CELL_TYPES.image].includes(selection[0].vertexType) &&
      // can't edit lines/connectors
      !selection[0].edgeType;

    return html`
      <div class="dropdown-content">
        ${showEdit
          ? html`
              <button
                @click=${this.#handleShowEditPopup}
                class="dropdown-item"
                tabindex="0"
              >
                Edit
              </button>
            `
          : nothing}
        <button
          @click=${() => {
            this.graphEditor.cloneSelected();
            this.#handleHideContextMenu();
          }}
          class="dropdown-item"
          tabindex="0"
        >
          Clone
        </button>
        <button
          @click=${() => {
            this.graphEditor.deleteSelected();
            this.#handleHideContextMenu();
          }}
          class="dropdown-item"
          tabindex="0"
        >
          Delete
        </button>
      </div>
    `;
  }

  render() {
    /** @todo The subtitle here should come from the module schema */
    return html`
      <omni-style>
        <div class="box module-container">
          <div class="header">
            <h2 class="title mb-5 is-3">Blueprint</h2>
            <h5 class="subtitle is-size-6 mb-0">
              This is a representation of all selected Paid and Owned channel
              activities, as well as active Earned environments showing how the
              campaign is structured.
            </h5>
          </div>
          ${this.#toolbarTemplate}
          <div class="graph-wrapper">
            <div class="canvas-wrapper">
              <div
                class=${classMap({
                  'canvas-loader': true,
                  active: this.imageLoading,
                })}
              >
                <omni-loading-indicator>
                  Please wait while content loads.
                </omni-loading-indicator>
              </div>
              <canvas ${ref(this.graphCanvasRef)}></canvas>
            </div>
            ${this.#contextMenuTemplate} ${this.#editPopupTemplate}
          </div>
          ${this.#toolboxTemplate}
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-blueprint', CampaignBlueprint);
